import moment, { Moment } from "moment"
import { CourseDates } from "../getCourseDates"
import { CourseVariant } from "../../prismic/Course"
import { TypeProductEntityFields } from "../ContentfulVzdelanibudoucnostiTypes"
import { daySymbolToAccusativeKey, getLocalizedWeekday, getWeekdayGenitive, getWeekdayLocative, weekdayAccusativeMap } from "./courseScheduleTextUtils"

moment.locale("cs")

const getStartAndEndTime = (firstCourseDay: Moment, lastCourseDay?: Moment): { startTime: string; endTime: string } | null => {
  if (firstCourseDay.hour() === 0 && firstCourseDay.minute() === 0) return null
  let end: Moment
  if (lastCourseDay && lastCourseDay.isSame(firstCourseDay, "day") && (lastCourseDay.hour() !== 0 || lastCourseDay.minute() !== 0)) {
    end = lastCourseDay
  } else {
    end = firstCourseDay.clone().add(60, "minutes") // Výchozí délka 60 minut
  }

  return {
    startTime: firstCourseDay.format("HH:mm"),
    endTime: end.format("HH:mm")
  }
}

const getFormattedDateRange = (first: Moment, last?: Moment): { startDate: string; endDate: string } => {
  const endDateMoment = last ?? first
  return {
    startDate: first.format("D. M.YYYY"),
    endDate: endDateMoment.format("D. M.YYYY")
  }
}

const countIsoWeekRange = (first: Moment, last: Moment): number => {
  const startYear = first.isoWeekYear()
  const endYear = last.isoWeekYear()
  const startWeek = first.isoWeek()
  const endWeek = last.isoWeek()
  return startYear === endYear ? endWeek - startWeek + 1 : moment(`${startYear}-12-28`).isoWeeksInYear() - startWeek + 1 + endWeek
}

const getWeekLabel = (count: number): string => {
  if (count === 1) return "1 týden"
  if (count >= 2 && count <= 4) return `${count} týdny`
  return `${count} týdnů`
}

const getMobileFromText = (date: Moment, timeRange: { startTime: string } | null): string => {
  const weekday = getWeekdayGenitive(date)
  return `Od ${weekday} ${date.format("D. M.YYYY")} ${timeRange?.startTime ?? ""}`.trim() // Přidán rok pro srozumitelnost
}

export const getCourseScheduleText = (
  courseDates: CourseDates | null,
  courseVariant: CourseVariant,
  firstCourseDayEntity?: Moment,
  lastCourseDayEntity?: Moment,
  isRecurring?: boolean,
  recurrenceFrequency?: ("daily" | "weekly")[],
  recurringDaysOfWeek?: ("friday" | "monday" | "saturday" | "sunday" | "thursday" | "tuesday" | "wednesday")[]
): { when: string | null; from: string | null } => {
  // --- LOGIKA PRO OPAKUJÍCÍ SE UDÁLOSTI ---
  if (isRecurring && Array.isArray(recurrenceFrequency) && recurrenceFrequency.length > 0 && firstCourseDayEntity) {
    const timeRange = getStartAndEndTime(firstCourseDayEntity, lastCourseDayEntity)
    const timeText = timeRange ? ` ${timeRange.startTime}–${timeRange.endTime}` : ` (čas bude upřesněn)`

    let recurrencePeriodInfo = ""
    if (lastCourseDayEntity) {
      if (!firstCourseDayEntity.isSame(lastCourseDayEntity, "day")) {
        recurrencePeriodInfo = ` od ${firstCourseDayEntity.format("D. M.YYYY")} do ${lastCourseDayEntity.format("D. M.YYYY")}`
      } else {
        recurrencePeriodInfo = ` ${getWeekdayLocative(firstCourseDayEntity)} ${firstCourseDayEntity.format("D. M.YYYY")}`
      }
    } else {
      recurrencePeriodInfo = ` od ${firstCourseDayEntity.format("D. M.YYYY")}` // Opakování bez definovaného konce
    }

    if (recurrenceFrequency.includes("daily")) {
      const whenText = `Každý den${timeText}${recurrencePeriodInfo}.`
      const fromText = `Denně${timeRange ? ` od ${timeRange.startTime}` : ""}${
        lastCourseDayEntity && !firstCourseDayEntity.isSame(lastCourseDayEntity, "day") ? ` (do ${lastCourseDayEntity.format("D.M.YY")})` : ""
      }`
      return { when: whenText, from: fromText }
    }

    if (recurrenceFrequency.includes("weekly") && Array.isArray(recurringDaysOfWeek) && recurringDaysOfWeek.length > 0) {
      const translatedDaysAccusative = recurringDaysOfWeek
        .map((daySymbol) => weekdayAccusativeMap[daySymbolToAccusativeKey[daySymbol.toLowerCase()]] || `Každý ${daySymbol}`)
        .filter(Boolean)

      let daysAccusativeText = ""
      if (translatedDaysAccusative.length === 1) {
        daysAccusativeText = translatedDaysAccusative[0]
      } else if (translatedDaysAccusative.length > 1) {
        const lastDay = translatedDaysAccusative.pop()
        daysAccusativeText = translatedDaysAccusative.join(" a ") + " a " + lastDay
      } else {
        daysAccusativeText = "Každý týden (nespecifikované dny)"
      }

      const whenText = `${daysAccusativeText}${timeText}${recurrencePeriodInfo}.`

      // Pro 'fromText' zobrazíme jen první den a datum začátku
      const firstRecurringDaySymbol = recurringDaysOfWeek[0]
      const firstRecurringDayAccusative = weekdayAccusativeMap[daySymbolToAccusativeKey[firstRecurringDaySymbol.toLowerCase()]]
      const firstDayWithoutKAZDE = firstRecurringDayAccusative
        ? firstRecurringDayAccusative.replace(/^\s*Každé\s*|^Každou\s*|^Každý\s*/, "")
        : firstRecurringDaySymbol.charAt(0).toUpperCase() + firstRecurringDaySymbol.slice(1)

      const fromText = `Od ${firstDayWithoutKAZDE} ${firstCourseDayEntity.format("D. M.YYYY")}${timeRange ? ` ${timeRange.startTime}` : ""}`

      return { when: whenText, from: fromText }
    }
  }

  // --- PŮVODNÍ LOGIKA PRO JEDNORÁZOVÉ NEBO SÉRIOVÉ UDÁLOSTI ---
  const effectiveFirstCourseDayForCalc = courseDates?.lessonDates?.[0] || firstCourseDayEntity
  if (!effectiveFirstCourseDayForCalc) return { when: null, from: null }

  const referenceTimeDay = firstCourseDayEntity || effectiveFirstCourseDayForCalc
  const timeRange = getStartAndEndTime(referenceTimeDay, lastCourseDayEntity)
  const mobileFrom = getMobileFromText(effectiveFirstCourseDayForCalc, timeRange)

  if ((!courseDates || !courseDates.lessonDates || courseDates.lessonDates.length === 0) && firstCourseDayEntity) {
    const dayName = getWeekdayLocative(firstCourseDayEntity)
    const dateText = firstCourseDayEntity.format("D. M.YYYY")
    const timeText = timeRange ? ` ${timeRange.startTime}–${timeRange.endTime}` : ` (čas bude upřesněn)`

    if (lastCourseDayEntity && !firstCourseDayEntity.isSame(lastCourseDayEntity, "day")) {
      const endDateText = lastCourseDayEntity.format("D. M.YYYY")
      return {
        when: `Událost ${getWeekdayLocative(firstCourseDayEntity)} ${firstCourseDayEntity.format("D. M.YYYY")} až ${getWeekdayLocative(
          lastCourseDayEntity
        )} ${endDateText}${timeText}.`,
        from: `Od ${firstCourseDayEntity.format("D.M.YY")} do ${lastCourseDayEntity.format("D.M.YY")}${timeRange ? ` ${timeRange.startTime}` : ""}`
      }
    }
    return {
      when: `Jednorázová událost ${dayName} ${dateText}${timeText}.`,
      from: mobileFrom
    }
  }

  if (!courseDates || !courseDates.lessonDates || courseDates.lessonDates.length === 0) {
    return { when: null, from: null }
  }

  const { lessonDates, skippedDates } = courseDates
  const firstLessonMoment = lessonDates[0]

  if (
    (courseVariant === CourseVariant.sampleCourse && lessonDates.length === 1) ||
    (firstCourseDayEntity && lastCourseDayEntity && firstCourseDayEntity.isSame(lastCourseDayEntity, "day"))
  ) {
    const dayName = getWeekdayLocative(firstLessonMoment)
    const dateText = firstLessonMoment.format("D. M.YYYY")
    const timeText = timeRange ? ` ${timeRange.startTime}–${timeRange.endTime}` : ` (čas bude upřesněn)`
    return {
      when: `Jednorázový kurz ${dayName} ${dateText}${timeText}.`,
      from: getMobileFromText(firstLessonMoment, timeRange)
    }
  }

  const lastActualLessonMoment = lessonDates[lessonDates.length - 1]
  const weekdayWithCorrectCase = getLocalizedWeekday(firstLessonMoment)
  const { startDate, endDate } = getFormattedDateRange(firstLessonMoment, lastActualLessonMoment)

  const totalWeeks = lastActualLessonMoment && firstLessonMoment ? countIsoWeekRange(firstLessonMoment, lastActualLessonMoment) : 0

  const skippedCount = skippedDates.length
  const skippedInfo =
    skippedCount > 0 ? `, bez ${skippedCount} státní${skippedCount === 1 ? "ho svátku" : skippedCount > 1 && skippedCount < 5 ? "ch svátků" : "ch svátků"}` : ""

  const timeText = timeRange ? ` ${timeRange.startTime}–${timeRange.endTime}` : ` (čas bude upřesněn)`

  return {
    when: `${weekdayWithCorrectCase}${timeText} od ${startDate} do ${endDate} (${
      totalWeeks > 0 ? getWeekLabel(totalWeeks) : "v rámci jednoho týdne"
    }${skippedInfo}).`,
    from: getMobileFromText(firstLessonMoment, timeRange)
  }
}
